import {
    AppPromoAction,
    AppPromoData,
    AppPromoPromotedSellableData,
    AppPromoSellableData
} from '../identity/member-summary-entity.model';
import {computed, inject, Injectable, Signal, signal, WritableSignal} from "@angular/core";
import {EntityData, FedeltaImageData} from "@fedelta-pos/data-client";
import {TranslateService} from "@ngx-translate/core";
import {MenuCategoryData, MenuItemData, MenuSellableData} from '../menu/menu-entity.model';
import {MenuModel} from '../menu/menu.model';
import {OrderItem} from '../order/order';
import {OrderModel} from '../order/order.model';
import {onEach} from '../utils/observable-utils';
import {ConfigurationModel} from '../configuration/configuration.model';
import {DeferredInjectable} from "../deferred-injection.service";
import {IdentityModel} from '../identity/identity.model';
import {OrderingContextModel} from '../ordering-context/ordering-context.model';
import {SessionModel} from '../session/session.model';

export class PromotedSellable {
    constructor(public sellableId: number,
                public featurePromotedMenuItems: boolean,
                public promotionalImage: FedeltaImageData,
                public useImageInPromoOnly: boolean,
                public menuItemFeatureTag: string) {
    }
}

export class PromoEvent {
    constructor(public promo: AppPromoData,
                public menuItem: MenuItemData) {
    }
}

@Injectable({providedIn: "root"})
export class PromoModel implements DeferredInjectable {
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private sessionModel: SessionModel = inject(SessionModel);
    private orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    private identityModel: IdentityModel = inject(IdentityModel);
    private translate: TranslateService = inject(TranslateService);
    private orderModel: OrderModel;
    private menuModel: MenuModel;

    public readonly activatedPromo: WritableSignal<AppPromoData> = signal<AppPromoData>(undefined);
    private readonly timesPromptedPerPromoId: WritableSignal<Map<string, number>> = signal<Map<string, number>>(new Map<string, number>());
    private readonly timesPromptedPerSellableId: WritableSignal<Map<number, number>> = signal<Map<number, number>>(new Map<number, number>());

    public readonly isFeaturedSellable: Signal<(sellableId: number) => boolean> = computed<(sellableId: number) => boolean>(() => {
        const promotedSellables: PromotedSellable[] = this.promotedSellables();
        return (sellableId: number): boolean => promotedSellables.some((promotedSellable: PromotedSellable) => promotedSellable.sellableId === sellableId && promotedSellable.featurePromotedMenuItems);
    });

    public readonly getPromotableMenuItem: Signal<(promoted: AppPromoPromotedSellableData, suppressProducts: boolean) => MenuItemData> = computed<(promoted: AppPromoPromotedSellableData, suppressProducts: boolean) => MenuItemData>(() => {
        const currentMenu: boolean = !!this.menuModel.currentMenu();
        const personallyOrderedSellableIds: Set<number> = this.orderModel.personallyOrderedSellableIds();
        const findMergedItemForSellable: (params: {sellableId: number; orderType: any}) => MenuItemData = this.menuModel.findMergedItemForSellable();
        const itemIsAvailable: (menuItem: MenuItemData) => boolean = this.menuModel.itemIsAvailable();
        const orderType: any = this.orderingContextModel.orderType();
        return (promoted: AppPromoPromotedSellableData, suppressProducts: boolean): MenuItemData => {
            if (!currentMenu || (suppressProducts && !promoted.promoteIfOrdered && personallyOrderedSellableIds.has(promoted.sellable.sellableId))) {
                return undefined;
            }
            const menuItem: MenuItemData = findMergedItemForSellable({
                sellableId: promoted.sellable.sellableId,
                orderType
            });
            return menuItem && itemIsAvailable(menuItem) ? menuItem : undefined;
        };
    });

    public readonly hasOrderedPromotedSellables: Signal<(promo: AppPromoData) => boolean> = computed<(promo: AppPromoData) => boolean>(() => {
        const personallyOrderedSellableIds: Set<number> = this.orderModel.personallyOrderedSellableIds();
        return (promo: AppPromoData): boolean => promo.promotedSellables.some((promoted: AppPromoPromotedSellableData) => personallyOrderedSellableIds.has(promoted.sellable.sellableId));
    });

    public readonly canPromoteSellablesAsCard: Signal<(promo: AppPromoData, suppressProducts: boolean) => boolean> = computed<(promo: AppPromoData, suppressProducts: boolean) => boolean>(() => {
        const getPromotableMenuItem: (promoted: AppPromoPromotedSellableData, suppressProducts: boolean) => MenuItemData = this.getPromotableMenuItem();
        return (promo: AppPromoData, suppressProducts: boolean): boolean => promo.promotedSellables.some((promoted: AppPromoPromotedSellableData) => !!getPromotableMenuItem(promoted, suppressProducts && promo.suppressPromoCardProducts));
    });

    public readonly canPromoteSellablesAsPopup: Signal<(promo: AppPromoData) => boolean> = computed<(promo: AppPromoData) => boolean>(() => {
        const hasOrderedPromotedSellables: (promo: AppPromoData) => boolean = this.hasOrderedPromotedSellables();
        const getPromotableMenuItem: (promoted: AppPromoPromotedSellableData, suppressProducts: boolean) => MenuItemData = this.getPromotableMenuItem();
        const timesPromptedPerSellableId: Map<number, number> = this.timesPromptedPerSellableId();
        return (promo: AppPromoData): boolean => !(promo.suppressPopupIfOrdered && hasOrderedPromotedSellables(promo)) &&
            promo.promotedSellables.some((promoted: AppPromoPromotedSellableData) => (timesPromptedPerSellableId.get(promoted.sellable.sellableId) ?? 0) === 0 && !!getPromotableMenuItem(promoted, promo.suppressPopupProducts));
    });

    public readonly canPromoteSellablesAsCategory: Signal<(promo: AppPromoData) => boolean> = computed<(promo: AppPromoData) => boolean>(() => {
        const hasOrderedPromotedSellables: (promo: AppPromoData) => boolean = this.hasOrderedPromotedSellables();
        const getPromotableMenuItem: (promoted: AppPromoPromotedSellableData, suppressProducts: boolean) => MenuItemData = this.getPromotableMenuItem();
        return (promo: AppPromoData): boolean => !(promo.suppressCategoryIfOrdered && hasOrderedPromotedSellables(promo)) &&
            promo.promotedSellables.some((promoted: AppPromoPromotedSellableData) => !!getPromotableMenuItem(promoted, promo.suppressCategoryProducts));
    });

    public readonly cardPromoIsTriggered: Signal<(promo: AppPromoData) => boolean> = computed<(promo: AppPromoData) => boolean>(() => {
        const canPromoteSellablesAsCard: (promo: AppPromoData, suppressProducts: boolean) => boolean = this.canPromoteSellablesAsCard();
        const hasOrderedTriggerSellable: (promo: AppPromoData) => boolean = this.hasOrderedTriggerSellable();
        return (promo: AppPromoData): boolean => promo.triggered && promo.showAppPromoCard &&
            hasOrderedTriggerSellable(promo) &&
            (!promo.promotedSellables.length || canPromoteSellablesAsCard(promo, true));
    });

    public readonly cardPromoIsSuppressed: Signal<(promo: AppPromoData) => boolean> = computed<(promo: AppPromoData) => boolean>(() => {
        const personalOrderStarted: boolean = this.orderModel.personalOrderStarted();
        const hasOrderedPromotedSellables: (promo: AppPromoData) => boolean = this.hasOrderedPromotedSellables();
        return (promo: AppPromoData): boolean => promo.suppressPromoCardIfAnyOrdered ? personalOrderStarted
            : promo.suppressPromoCardIfOrdered ? hasOrderedPromotedSellables(promo)
                : false;
    });

    public readonly promotedFeatureTag: Signal<(category: MenuCategoryData, menuItem: MenuItemData) => string> = computed<(category: MenuCategoryData, menuItem: MenuItemData) => string>(() => {
        const promotedSellables: PromotedSellable[] = this.promotedSellables();
        return (category: MenuCategoryData, menuItem: MenuItemData): string => {
            // Promoted category - use its feature tag if specified
            if (category && category.itemFeatureTag) {
                menuItem.promotedFeatureTag = category.itemFeatureTag;
                return menuItem.promotedFeatureTag;
            }

            if (!promotedSellables) {
                return undefined;
            }

            let isPromoted: boolean = false;
            for (const promotedSellable of promotedSellables) {
                if (promotedSellable.featurePromotedMenuItems && promotedSellable.sellableId === menuItem.sellable.sellableId) {
                    isPromoted = true;
                    if (promotedSellable.menuItemFeatureTag) {
                        menuItem.promotedFeatureTag = promotedSellable.menuItemFeatureTag;
                        return menuItem.promotedFeatureTag;
                    }
                }
            }
            menuItem.promotedFeatureTag = isPromoted ? this.translate.instant("woa.label.order.featured") : undefined;
            return menuItem.promotedFeatureTag;
        };
    });

    public readonly promotionalImage: Signal<(category: MenuCategoryData, sellableId: number, forPromo?: boolean) => FedeltaImageData> = computed<(category: MenuCategoryData, sellableId: number, forPromo?: boolean) => FedeltaImageData>(() => {
        const promotedSellables: PromotedSellable[] = this.promotedSellables();
        return (category: MenuCategoryData, sellableId: number, forPromo?: boolean): FedeltaImageData => {
            if (forPromo === undefined) {
                forPromo = category && category.id == -1;
            }
            const promotedSellable: PromotedSellable = promotedSellables.find((promotedSellable: PromotedSellable) => promotedSellable.sellableId === sellableId && (forPromo || !promotedSellable.useImageInPromoOnly));
            return promotedSellable && promotedSellable.promotionalImage && promotedSellable.promotionalImage.id ? promotedSellable.promotionalImage : undefined;
        };
    });

    // Prevent re-prompting after a "checkout" action is deferred.
    // This flag is cleared when a non-deferred checkout action is started.
    // Note: this does NOT stop multiple checkout promos - that is done by the limit on the individual promo.
    private promoPromptedForCurrentCheckout: boolean = false;

    private timesPromosPrompted: number = 0;

    constructor() {
        console.log("--- PromoModel");

        onEach(this.sessionModel.sessionReset$, () => {
            this.promoPromptedForCurrentCheckout = false;
            this.timesPromosPrompted = 0;
            this.timesPromptedPerPromoId.set(new Map<string, number>());
            this.timesPromptedPerSellableId.set(new Map<number, number>());
        });
    }

    public inject(): void {
        this.orderModel = inject(OrderModel);
        this.menuModel = inject(MenuModel);
    }

    public readonly triggeredSellablesOrdered: Signal<Set<MenuSellableData>> = computed(
        () => new Set([...this.orderModel.personallyOrderedSellables()].filter((sellable: MenuSellableData) =>
            this.identityModel.currentMember()?.promos.some((promo: AppPromoData) => promo.isTriggeredBySellable(sellable)))),
        {equal: (a: Set<MenuSellableData>, b: Set<MenuSellableData>) => a.size === b.size && [...a].every(item => b.has(item))});

    public readonly hasOrderedTriggerSellable: Signal<(promo: AppPromoData) => boolean> = computed(() => {
        const personallyOrderedSellables: Set<MenuSellableData> = this.orderModel.personallyOrderedSellables();
        return (promo: AppPromoData): boolean =>
            [...personallyOrderedSellables].some((sellable: MenuSellableData) => promo.isTriggeredBySellable(sellable));
    });

    private readonly hasJustOrderedTriggerSellable: Signal<(promo: AppPromoData) => boolean> = computed(() => {
        const personallyJustOrderedSellables: Set<MenuSellableData> = this.orderModel.personallyJustOrderedSellables();
        return (promo: AppPromoData): boolean =>
            [...personallyJustOrderedSellables].some((sellable: MenuSellableData) => promo.isTriggeredBySellable(sellable));
    });

    /**
     * The list of currently promoted sellables and their corresponding promotional image and/or feature tag.
     * "Currently promoted" sellables are those promoted by an untriggered promo, or by a promo triggered by a product that has been ordered.
     */
    private readonly promotedSellables: Signal<PromotedSellable[]> = computed(() =>
        this.identityModel.currentMember()?.promos
            .filter((promo: AppPromoData) => promo.isAvailableForOrderType(this.orderingContextModel.orderType()) &&
                promo.promotedSellables.length &&
                (!promo.triggered || this.hasOrderedTriggerSellable()(promo)))
            .flatMap((promo: AppPromoData) => promo.promotedSellables.map((promotedSellable: AppPromoPromotedSellableData) =>
                new PromotedSellable(promotedSellable.sellable.sellableId, promo.featurePromotedMenuItems, promotedSellable.productImage, promo.itemImagesInPromoOnly, promo.menuItemFeatureTag))) ?? []);

    public readonly availableCheckoutPopupPromos: Signal<AppPromoData[]> = computed(() =>
        (!this.identityModel.currentMember() ||
        (this.configurationModel.app()?.promoPopupLimit && this.timesPromosPrompted >= this.configurationModel.app()?.promoPopupLimit) ? [] :
            this.identityModel.currentMember().promos.filter((promo: AppPromoData) =>
                promo.isAvailableForOrderType(this.orderingContextModel.orderType()) &&
                promo.showPopupOnCheckout &&
                (!promo.popupLimit || (this.timesPromptedPerPromoId().get(promo.id) ?? 0) < promo.popupLimit) &&
                (!promo.triggered || this.hasOrderedTriggerSellable()(promo)) &&
                (!promo.promotedSellables.length || this.canPromoteSellablesAsPopup()(promo)))));

    /**
     * Selects a popup promo and menu item from the supplied list of promos based on the most recently ordered items from the current order.
     *
     * Rules for picking available popup promos:
     * 1. The promo must have been prompted fewer times than its popup limit (if set);
     * 2. The promo must be triggered and configured to "popupWhenOrdered";
     * 3. The promo must be triggered by a product which has just been ordered;
     * 4. If the promo has promoted sellables:
     *      a) None of the promoted sellables may not be already on the order (if "suppressPopupIfOrdered" is set);
     *      b) One or more promoted sellables must be:
     *          i)   not already prompted; and
     *          ii)  not on the order; and
     *          iii) on the menu;
     * 5. The promo must have already been prompted a number of times equal to the minimum for all promos meeting criteria 1 through 4.
     *
     * Additional filtering is then done to filter out promoted sellables which have already been prompted, or are not on the menu.
     */
    public selectTriggeredPopupWhenOrderedPromo(promos: AppPromoData[]): {
        promo: AppPromoData,
        menuItem?: MenuItemData
    } {
        if (this.configurationModel.app()?.promoPopupLimit && this.timesPromosPrompted >= this.configurationModel.app()?.promoPopupLimit) {
            return undefined;
        }

        promos = [...promos];

        // Add individual promos specified directly on the recently ordered items
        if (this.configurationModel.isConsumerActor()) {
            this.orderModel.recentlyOrderedItems().forEach((item: OrderItem) => {
                if (item.sellable().promotedSellableId && (this.timesPromptedPerSellableId().get(item.sellable().promotedSellableId) ?? 0) === 0) {
                    const promo: AppPromoData = EntityData.create(AppPromoData);
                    promo.id = item.sellable().sellableId.toString();
                    promo.name = this.menuModel.getMenuItemDisplayName(item.menuItem());
                    promo.triggered = true;
                    const triggerSellable: AppPromoSellableData = EntityData.create(AppPromoSellableData);
                    triggerSellable.sellableId = item.sellable().sellableId;
                    promo.triggerSellables = [triggerSellable];
                    const promotedSellable: AppPromoPromotedSellableData = EntityData.create(AppPromoPromotedSellableData);
                    promotedSellable.sellable.sellableId = item.sellable().promotedSellableId;
                    promotedSellable.promoteIfOrdered = false;
                    promo.promotedSellables = [promotedSellable];
                    promo.showPopupWhenOrdered = true;
                    promo.popupAction = AppPromoAction.ORDER_PRODUCT;
                    promo.suppressPopupIfOrdered = true;
                    promo.suppressPopupProducts = true;
                    promo.popupLimit = 1;
                    promo.showDescriptionOnPopup = true;
                    promos.push(promo);

                    // Prioritise individual promos
                    if (!this.timesPromptedPerPromoId().has(promo.id)) {
                        this.timesPromptedPerPromoId.update(map => new Map(map).set(promo.id, -1));
                    }
                }
            });
        }

        return !this.orderModel.orderHasItems() ? undefined : this.selectPopupPromo(
            promos.filter((promo: AppPromoData) =>
                promo.isAvailableForOrderType(this.orderingContextModel.orderType()) &&
                promo.triggered && promo.showPopupWhenOrdered &&
                (!promo.popupLimit || (this.timesPromptedPerPromoId().get(promo.id) ?? 0) < promo.popupLimit) &&
                this.hasJustOrderedTriggerSellable()(promo) &&
                (!promo.promotedSellables.length || this.canPromoteSellablesAsPopup()(promo))));
    }

    /**
     * Selects a popup promo and menu item based on the most recently ordered items in the current order.
     *
     * Rules for picking available popup promos:
     * 1. The promo must have been prompted fewer times than its popup limit (if set);
     * 2. The promo must be triggered and configured to "popupOnCheckout";
     * 3. The promo must either be untriggered or be triggered by a product in the order;
     * 4. If the promo has promoted sellables:
     *      a) None of the promoted sellables may be already on the order (if "suppressPopupIfOrdered" is set);
     *      b) One or more promoted sellables must be:
     *          i)   not already prompted; and
     *          ii)  not on the order; and
     *          iii) currently on the menu (respecting schedules);
     * 5. The promo must have already been prompted a number of times equal to the minimum for all promos meeting criteria 1 through 4.
     *
     * Additional filtering is then done to filter out promoted sellables which have already been prompted, or are not on the menu.
     */
    public selectCheckoutPopupPromo(): { promo: AppPromoData, menuItem?: MenuItemData } {
        if (this.promoPromptedForCurrentCheckout) {
            return undefined;
        }
        const selection: {
            promo: AppPromoData,
            menuItem?: MenuItemData
        } = this.selectPopupPromo(this.availableCheckoutPopupPromos());
        if (selection) {
            this.promoPromptedForCurrentCheckout = true;
        }
        return selection;
    }

    public notifyCheckoutStarted(deferred: boolean): void {
        if (!deferred) {
            this.promoPromptedForCurrentCheckout = false;
        }
    }

    private selectPopupPromo(promos: AppPromoData[]): { promo: AppPromoData, menuItem?: MenuItemData } {
        if (!promos.length) {
            return undefined;
        }

        const minTimesPrompted: number = Math.min(...promos.map((promo: AppPromoData) => this.timesPromptedPerPromoId().get(promo.id) ?? 0));
        const lfuPromos: AppPromoData[] = promos.filter((promo: AppPromoData) => (this.timesPromptedPerPromoId().get(promo.id) ?? 0) === minTimesPrompted);
        const promo: AppPromoData = lfuPromos[Math.floor(Math.random() * lfuPromos.length)];
        if (!promo.promotedSellables.length) {
            this.timesPromosPrompted++;
            this.timesPromptedPerPromoId.update(map => new Map(map).set(promo.id, Math.max(1, (map.get(promo.id) ?? 0) + 1)));
            return {promo: promo};
        }
        for (const promoted of promo.promotedSellables) {
            if ((this.timesPromptedPerSellableId().get(promoted.sellable.sellableId) ?? 0) === 0) {
                const menuItem: MenuItemData = this.getPromotableMenuItem()(promoted, promo.suppressPopupProducts);
                if (menuItem) {
                    this.timesPromosPrompted++;
                    this.timesPromptedPerPromoId.update(map => new Map(map).set(promo.id, Math.max(1, (map.get(promo.id) ?? 0) + 1)));
                    this.timesPromptedPerSellableId.update(map => new Map(map).set(promoted.sellable.sellableId, (map.get(promoted.sellable.sellableId) ?? 0) + 1));
                    return {promo: promo, menuItem: menuItem};
                }
            }
        }

        return undefined;
    }
}
